import CaptainFaqLearningAPI from 'dashboard/api/captain/faqLearning';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const SET_SUGGESTIONS = 'SET_SUGGESTIONS';
const SET_OBSERVATIONS = 'SET_OBSERVATIONS';
const SET_SUGGESTIONS_META = 'SET_SUGGESTIONS_META';
const SET_OBSERVATIONS_META = 'SET_OBSERVATIONS_META';
const SET_SELECTED_SUGGESTION = 'SET_SELECTED_SUGGESTION';
const SET_UI_FLAGS = 'SET_UI_FLAGS';
const RESET = 'RESET';

let suggestionsRequestVersion = 0;
let observationsRequestVersion = 0;
let detailRequestVersion = 0;

const initialState = () => ({
  suggestions: [],
  observations: [],
  suggestionsMeta: { page: 1, per_page: 25, total_count: 0, total_pages: 0 },
  observationsMeta: { page: 1, per_page: 25, total_count: 0, total_pages: 0 },
  selectedSuggestion: null,
  uiFlags: {
    fetchingSuggestions: false,
    fetchingObservations: false,
    fetchingDetail: false,
    mutating: false,
  },
});

const runMutation = async ({ commit }, request) => {
  commit(SET_UI_FLAGS, { mutating: true });
  try {
    const response = await request();
    commit(SET_SELECTED_SUGGESTION, response.data.payload);
    return response.data;
  } catch (error) {
    return throwErrorMessage(error);
  } finally {
    commit(SET_UI_FLAGS, { mutating: false });
  }
};

const runObservationMutation = async ({ commit }, request) => {
  commit(SET_UI_FLAGS, { mutating: true });
  try {
    const response = await request();
    return response.data;
  } catch (error) {
    return throwErrorMessage(error);
  } finally {
    commit(SET_UI_FLAGS, { mutating: false });
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters: {
    getSuggestions: state => state.suggestions,
    getObservations: state => state.observations,
    getSuggestionsMeta: state => state.suggestionsMeta,
    getObservationsMeta: state => state.observationsMeta,
    getSelectedSuggestion: state => state.selectedSuggestion,
    getUIFlags: state => state.uiFlags,
  },
  mutations: {
    [SET_SUGGESTIONS](state, records) {
      state.suggestions = records;
    },
    [SET_OBSERVATIONS](state, records) {
      state.observations = records;
    },
    [SET_SUGGESTIONS_META](state, meta) {
      state.suggestionsMeta = meta;
    },
    [SET_OBSERVATIONS_META](state, meta) {
      state.observationsMeta = meta;
    },
    [SET_SELECTED_SUGGESTION](state, suggestion) {
      state.selectedSuggestion = suggestion;
    },
    [SET_UI_FLAGS](state, flags) {
      state.uiFlags = { ...state.uiFlags, ...flags };
    },
    [RESET](state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async fetchSuggestions({ commit }, params) {
      suggestionsRequestVersion += 1;
      const requestVersion = suggestionsRequestVersion;
      commit(SET_UI_FLAGS, { fetchingSuggestions: true });
      try {
        const response = await CaptainFaqLearningAPI.suggestions(params);
        if (requestVersion !== suggestionsRequestVersion) return response.data;

        commit(SET_SUGGESTIONS, response.data.payload);
        commit(SET_SUGGESTIONS_META, response.data.meta);
        return response.data;
      } catch (error) {
        if (requestVersion === suggestionsRequestVersion) {
          return throwErrorMessage(error);
        }
        return undefined;
      } finally {
        if (requestVersion === suggestionsRequestVersion) {
          commit(SET_UI_FLAGS, { fetchingSuggestions: false });
        }
      }
    },
    async fetchObservations({ commit }, params) {
      observationsRequestVersion += 1;
      const requestVersion = observationsRequestVersion;
      commit(SET_UI_FLAGS, { fetchingObservations: true });
      try {
        const response = await CaptainFaqLearningAPI.observations(params);
        if (requestVersion !== observationsRequestVersion) return response.data;

        commit(SET_OBSERVATIONS, response.data.payload);
        commit(SET_OBSERVATIONS_META, response.data.meta);
        return response.data;
      } catch (error) {
        if (requestVersion === observationsRequestVersion) {
          return throwErrorMessage(error);
        }
        return undefined;
      } finally {
        if (requestVersion === observationsRequestVersion) {
          commit(SET_UI_FLAGS, { fetchingObservations: false });
        }
      }
    },
    async fetchSuggestion({ commit }, params) {
      detailRequestVersion += 1;
      const requestVersion = detailRequestVersion;
      commit(SET_UI_FLAGS, { fetchingDetail: true });
      try {
        const response = await CaptainFaqLearningAPI.suggestion(params);
        if (requestVersion !== detailRequestVersion) return response.data;

        commit(SET_SELECTED_SUGGESTION, response.data.payload);
        return response.data;
      } catch (error) {
        if (requestVersion === detailRequestVersion) {
          return throwErrorMessage(error);
        }
        return undefined;
      } finally {
        if (requestVersion === detailRequestVersion) {
          commit(SET_UI_FLAGS, { fetchingDetail: false });
        }
      }
    },
    review(context, params) {
      return runMutation(context, () => CaptainFaqLearningAPI.review(params));
    },
    reprocess(context, params) {
      return runMutation(context, () =>
        CaptainFaqLearningAPI.reprocess(params)
      );
    },
    discard(context, params) {
      return runMutation(context, () => CaptainFaqLearningAPI.discard(params));
    },
    reprocessObservation(context, params) {
      return runObservationMutation(context, () =>
        CaptainFaqLearningAPI.reprocessObservation(params)
      );
    },
    discardObservation(context, params) {
      return runObservationMutation(context, () =>
        CaptainFaqLearningAPI.discardObservation(params)
      );
    },
    reset({ commit }) {
      suggestionsRequestVersion += 1;
      observationsRequestVersion += 1;
      detailRequestVersion += 1;
      commit(RESET);
    },
    clearSelectedSuggestion({ commit }) {
      detailRequestVersion += 1;
      commit(SET_SELECTED_SUGGESTION, null);
    },
  },
};
